import type {
  LockerverseCatalog,
  LockerverseCheckoutEstimate,
  LockerverseCheckoutPricingInput,
  LockerversePreparedCheckout,
} from "@lockerverse/sdk/checkout";
import type { CheckoutFieldValues } from "./checkout-fields.tsx";
import { parseCustomTipCents } from "./payment-input.ts";

const INVALID_PROMO_MESSAGE =
  "This promo code is invalid or cannot be applied.";

export function getTipCents(
  subtotalCents: number,
  values: Pick<CheckoutFieldValues, "customTip" | "tipPercent">
) {
  if (values.tipPercent === "custom") {
    if (!values.customTip.trim()) {
      return { cents: 0, success: true } as const;
    }
    return parseCustomTipCents(values.customTip);
  }
  return {
    cents: Math.round((subtotalCents * Number(values.tipPercent)) / 100),
    success: true,
  } as const;
}

export function buildQuoteRequest(
  catalog: LockerverseCatalog,
  fieldValues: CheckoutFieldValues,
  email: string,
  tipCents: number
) {
  const request: LockerverseCheckoutPricingInput = {};
  if (email) {
    request.email = email;
  }
  if (catalog.widget.fields.serviceFee) {
    request.includeServiceFee = fieldValues.includeServiceFee;
  }
  if (catalog.widget.fields.promoCode && fieldValues.promoCode.trim()) {
    request.promoCode = fieldValues.promoCode.trim();
  }
  if (catalog.widget.fields.tipping) {
    request.tipCents = tipCents;
  }
  return request;
}

export function getLocalQuotePreview(
  catalog: LockerverseCatalog,
  fieldValues: CheckoutFieldValues,
  quote: LockerverseCheckoutEstimate | LockerversePreparedCheckout
) {
  const parsedTip = catalog.widget.fields.tipping
    ? getTipCents(quote.subtotalCents, fieldValues)
    : ({ cents: 0, success: true } as const);
  if (!parsedTip.success) {
    return { parsedTip, totalCents: null };
  }

  const serviceFeeCents =
    catalog.widget.fields.serviceFee && fieldValues.includeServiceFee
      ? quote.serviceFeeCents ||
        Math.round(
          (quote.subtotalCents *
            catalog.pricingPolicy.serviceFeeRateBasisPoints) /
            10_000
        )
      : 0;
  return {
    parsedTip,
    totalCents:
      quote.totalCents -
      quote.serviceFeeCents -
      quote.tipCents +
      serviceFeeCents +
      parsedTip.cents,
  };
}

export function getQuoteRefreshSuccess(
  quote: LockerversePreparedCheckout,
  validatePromo: boolean
) {
  return {
    displayedTotalCents: quote.totalCents,
    promoError:
      validatePromo && quote.appliedDiscount?.source !== "promo_code"
        ? INVALID_PROMO_MESSAGE
        : undefined,
  };
}

export function getQuoteRefreshFailure<ErrorValue>(
  error: ErrorValue,
  validatePromo: boolean
) {
  let paymentError: string | undefined;
  if (!validatePromo) {
    paymentError =
      error instanceof Error
        ? error.message
        : "The order total could not be updated.";
  }
  return {
    paymentError,
    promoError: validatePromo ? INVALID_PROMO_MESSAGE : undefined,
  };
}
